// The Lender Hub's records as the frontend sees them (LH-F1). These shapes are the backend's contract once the
// frontend is accepted (frontend-build-plan.md §3, LH-L lock). Figures in the fixtures are examples.

export type Role = 'admin' | 'editor' | 'reviewer' | 'analyst' | 'mis_uploader'

export interface User {
  id: string
  name: string
  email: string
  role: Role
  twoStep: boolean
  lastSignInAt: string | null
}

export type LenderStatus = 'draft' | 'active' | 'off'
export type Surface = 'website' | 'app'
export type WhitelistState = 'confirmed' | 'requested' | 'not_needed'

export interface KeyFacts {
  amountMin: number
  amountMax: number
  tenureMinMonths: number
  tenureMaxMonths: number
  rateMinPa: number
  rateMaxPa: number
  processingFee: string
  aprMin: number
  aprMax: number
  ageMin: number
  ageMax: number
  minIncomeMonth: number
  documents: string[]
  disbursal: string
  extra: string[]
}

/** The kind of loan (LD-1): the website's filter chips and Apply now's loan type; paylater = Buy now, pay later. */
export type LoanType = 'personal' | 'business' | 'gold' | 'paylater' | 'other'

export interface Product {
  slug: string
  name: string
  loanType: LoanType
  active: boolean
  keyFacts: KeyFacts
  /** file name in hub-media; null = the lender-level default */
  kfsFile: string | null
  /** the service endpoint behind this product; null = information only */
  endpointId: string | null
  endpointState: 'live' | 'uat' | 'none'
}

export interface Workflow {
  name: string
  version: string
  endpointId: string
  state: 'live' | 'draft' | 'previous' | 'suspended'
}

/** What kind of lender (PF-1), a label only: an LSP / DLA platform (MoneyView), an NBFC, a bank or an aggregator. */
export type PartnerType = 'lsp' | 'nbfc' | 'bank' | 'aggregator'

/** One regulated lender behind a partner: an LSP's NBFC / bank, or the bank an NBFC co-lends with. The first is named on the card. */
export interface LendingPartner {
  legalName: string
  kind: 'bank' | 'nbfc'
  rbiCor: string
  grievanceUrl: string
}

export interface Lender {
  id: string
  slug: string
  name: string
  legalName: string
  rbiCor: string
  website: string
  partnerType: PartnerType
  /** link = the customer goes to the partner's website / app; api = we send the application */
  connection: 'link' | 'api' | 'manual'
  lenders: LendingPartner[]
  /** a logo is uploaded (read only on the API) */
  hasLogo: boolean
  /** written permission to show the logo (LD-1); null = none on file: Website and Apply now cannot be published */
  logoPermission: { date: string; note: string } | null
  /** the digital lending app's name (RBI DLA); empty = none */
  appName: string
  /** Apply now 2.10 for an API lender: "a soft check, no effect on your credit score"; empty = none */
  creditCheckNote: string
  /** LD-3: added by Apply now to every Continue link (moved from App admin); {refId} = the lead's reference */
  linkParams: Record<string, string>
  grievance: { name: string; email: string; phone: string; designation: string; hours: string; address: string }
  /** the tile colour behind the letters until a logo is uploaded */
  brand: { color: string }
  shortLine: string
  links: { web: string; play: string; appstore: string; privacy: string; grievancePolicy: string; fairPractices: string }
  env: { uatBaseUrl: string; prodBaseUrl: string; keysSet: boolean; keyNames: string[] }
  /** tab 5 · Env variables (LH-F5): what the batch code reads as ctx.env.NAME; .env in the playground, the vault in production */
  envVars: EnvVar[]
  ipWhitelist: { state: WhitelistState; date: string | null }
  usedBy: Surface[]
  status: LenderStatus
  activeSince: string | null
  offReason: string | null
  products: Product[]
  liveBatch: string | null
  draftBatch: string | null
  workflows: Workflow[]
  createdAt: string
  updatedAt: string
}

/**
 * One variable of a lender (tab 5). `hub` = set by the Hub itself (LENDER_BASE_URL from Lender info, LENDER_TOKEN from
 * the token endpoint); `code` = found in the batch code by discovery, waiting for a value; `manual` = typed in.
 * Values are never in git, in storage or in a log; the demo data holds placeholders only.
 */
export interface EnvVar {
  name: string
  uat: string | null
  prod: string | null
  /** masked on screen, shown once when set */
  secret: boolean
  source: 'hub' | 'code' | 'manual'
  /** files that read it (from the manifest) */
  usedIn: string[]
  updatedAt: string
  updatedBy: string
}

export interface HistoryEntry {
  id: string
  at: string
  who: string
  lenderSlug: string
  where: string
  what: string
}

export interface Alert {
  id: string
  at: string
  lenderSlug: string | null
  severity: 'danger' | 'warning' | 'info'
  text: string
  acknowledgedAt: string | null
  /** the alert rule that raised it (F3-6); info = a notice (batch live, roll-back, …) */
  kind?: 'new_code' | 'error_rate' | 'lender_off' | 'info'
}

/** The event other services receive on the single switch (contract events/lender-status-changed.v1, to write). */
export interface LenderStatusChanged {
  slug: string
  status: LenderStatus
  reason: string | null
  at: string
  by: string
}

// ---- LD-1: Where it shows (published copies for the website and Apply now) ---------------------------------------

export type Channel = 'website' | 'applynow'
/** One ★ field a place needs before Publish; the Hub API decides (one rule file), the portal shows it. */
export interface ListingCheck {
  key: string
  label: string
  ok: boolean
  fix: 'basics' | 'details' | 'logo' | 'products' | 'api'
}
/** GET /admin/v1/lenders/{slug}/listing */
export interface LenderListing {
  slug: string
  channels: Channel[]
  version: number | null
  publishedAt: string | null
  publishedBy: string | null
  changedSincePublish: boolean
  places: { channel: Channel; label: string; published: boolean; ready: boolean; checks: ListingCheck[] }[]
  versions: { version: number; action: 'publish' | 'unpublish'; channel: Channel; at: string; by: string }[]
  lenderActive: boolean
}

/** What a required field is called on the Review step of Add a lender. */
export interface RequiredCheck {
  key: string
  label: string
  ok: boolean
  step: 1 | 2 | 3 | 4
}

// ---- LH-F2: lender APIs as code, API batches, the playground ----------------------------------------------------

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
/** How the lender's API is authenticated. `token_source` = this API gives the bearer token the others use (F2-6). */
export type AuthKind = 'none' | 'bearer' | 'api_key_header' | 'basic' | 'token_source'
export type ApiStatus = 'active' | 'old_kept' | 'old_unused'
export type ApiWhitelistState = 'done' | 'requested' | 'lender_removed' | 'not_needed'

/** The record over one endpoint of the lender's batch (3.1). The code itself is the file under src/api/ in the batch. */
export interface LenderApi {
  id: string
  lenderSlug: string
  name: string
  /** the endpoint's route in the batch, from its file's place under src/api/ (src/lib/endpoints.ts): /v2/dedupe */
  endpoint: string
  /** the lender's own method and path the endpoint calls */
  method: HttpMethod
  path: string
  auth: AuthKind
  /** free text shown next to the auth kind, e.g. "partner_id + secret → bearer 55 min" */
  authNote: string
  whitelist: { state: ApiWhitelistState; date: string | null }
  status: ApiStatus
  /** which workflows (or "monitor, every 60 s", "all (auto)") use it */
  usedIn: string
  lastCall: { at: string; httpStatus: number } | null
  /** J2: what it answers (none = no offer; one = a single offer; list = several offers, an LSP) */
  answer: ApiAnswer
  /** J2: "Fields this API needs": the extra fields beyond the standard input, as the lender names and codes them */
  fields: ApiField[]
}

export type ApiAnswer = 'none' | 'one' | 'list'
/** One extra field a lender API needs: our Apply now library key → the lender's own name, and our option value → its code. */
export interface ApiField {
  key: string
  required: boolean
  lenderKey: string
  codes: Record<string, string>
}

export interface CodeFile {
  path: string
  content: string
}

/** One endpoint as discovery found it (hub-discover in the sandbox, or src/lib/manifest.ts on the text). */
export interface ManifestEndpoint {
  /** the route: /v2/dedupe */
  path: string
  method: HttpMethod
  /** the file under src/api/ (or api/) */
  file: string
  outcomes: string[]
  title: string | null
  tokenSource: boolean
}

/** .hub/manifest.json: generated, never written by hand; what the canvas, 3.1, the Env tab and Build read. */
export interface Manifest {
  generatedAt: string
  source: 'agent' | 'static'
  endpoints: ManifestEndpoint[]
  /** the variables the code reads, with the files that read them */
  env: { name: string; files: string[] }[]
  dependencies: Record<string, string>
  problems: { file: string; text: string }[]
}

/** A test event (Lambda word): one sample lender reply for one endpoint and the outcome its handler must return. */
export interface TestEvent {
  id: string
  /** the endpoint's route: /v2/dedupe */
  endpoint: string
  name: string
  reply: { status: number; body: unknown }
  expectedOutcome: string
  /** sample = written by us; captured = added from a real lender reply with "Add as a test" */
  source: 'sample' | 'captured'
}

export interface TestResult {
  testId: string
  endpoint: string
  name: string
  expectedOutcome: string
  actualOutcome: string | null
  ok: boolean
  log: string[]
  error: string | null
  value: unknown
  ms: number
  /** the system check on the handler's outcome and on the call it gave ctx.lender() (the SDK, src/lib/outputCheck.ts) */
  issues?: { level: 'error' | 'warning'; text: string }[]
  /** the call the handler made (headers masked) and the reply it got */
  call?: { method: string; url: string; headers?: Record<string, string>; body?: unknown; timeoutMs?: number }
  reply?: { status: number; body: unknown; ms: number; error?: string }
  /** variables the code read that the lender does not have (Env variables tab) */
  envMissing?: string[]
}

/** One way (G6): draft → tests passed → built → live; then previous (kept for roll-back) → retired. */
export type BatchStatus = 'draft' | 'tests_passed' | 'built' | 'live' | 'previous' | 'retired'

export interface Commit {
  sha: string
  message: string
  who: string
  at: string
  files: string[]
}

/** One API batch = one version of all the lender's API code (G3), `<lender>.batchApi.<version>`. */
export interface Batch {
  id: string
  lenderSlug: string
  /** BP-1: the product this batch is for (`<lender>.<product>`); null / left out = a whole-lender batch from before */
  productSlug?: string | null
  version: string
  status: BatchStatus
  madeFrom: string | null
  /** the Node service: package.json, src/server.js, src/app.js, src/api/<route>.js, src/services/, README.md … (node_modules and .hub live in the sandbox only) */
  files: CodeFile[]
  /** generated by discovery: endpoints, their outcomes, the variables the code reads */
  manifest: Manifest
  tests: TestEvent[]
  /** the last Run all tests on this version */
  testRun: { passed: number; total: number; at: string; by: string } | null
  git: { branch: string; tag: string | null; commit: string }
  /** the built bundle in storage (G12), null until Build */
  bundle: { key: string; checksum: string; builtAt: string; builtBy: string } | null
  commits: Commit[]
  createdBy: string
  createdAt: string
  updatedAt: string
  madeLiveAt: string | null
}

/** A lender reply code or reply shape seen in testing or production (G11), counted, with "Add as a test". */
export interface CapturedCode {
  id: string
  lenderSlug: string
  /** the code, or a short description of the shape, e.g. "{ status, offers: {…} }" */
  code: string
  /** the endpoint that saw it (route) */
  endpoint: string
  uat: number
  prod: number
  lastSeen: string
  known: 'known' | 'new' | 'new_shape'
  /** the outcome parse() gives it when known */
  outcome: string | null
  alertId: string | null
  /** the reply as captured (masked), what "Add as a test" uses */
  sampleReply: { status: number; body: unknown }
}

// ---- LH-F3: workflows on the canvas, service endpoints, runs, settings -----------------------------------------

/** The lender node pack (F3-1). Start = the standard input (PI info); API = one batch file; Answer = one of seven. */
export type FlowNodeKind = 'start' | 'api' | 'decision' | 'wait' | 'answer'
export const ANSWER_KINDS = ['offer', 'approved', 'redirect', 'pending', 'rejected', 'duplicate', 'error'] as const
export type AnswerKind = (typeof ANSWER_KINDS)[number]

/** A condition without code (Decision nodes and conditional lines): field · operator · value. */
export interface Condition {
  /** a path into the run context: applicant.incomeMonth, prev.reply_code, prev.offers.length, steps.offers … */
  field: string
  op: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains' | 'exists' | 'missing'
  value: string
}

export interface FlowNode {
  id: string
  kind: FlowNodeKind
  name: string
  pos: { x: number; y: number }
  /** api: the endpoint it calls, by route (/v2/dedupe) in the batch the workflow uses */
  endpoint?: string
  /** api: seconds to wait for the lender; null = the workflow's "wait per call" */
  waitS?: number | null
  /** api: extra tries on error (timeout / 5xx) */
  retries?: number
  /** decision: the condition; outputs yes / no */
  condition?: Condition
  /** wait: seconds before going on (polling) */
  seconds?: number
  /** answer: which standard answer and the message our user sees */
  answer?: AnswerKind
  message?: string
}

/** A line from one output port of a node to another node; with a condition it is dashed and tried first. */
export interface FlowEdge {
  id: string
  from: string
  /** the output port (outcome) it leaves from */
  when: string
  to: string
  condition?: Condition | null
}

/** One way like the batch: draft → paths passed → live; replaced live → previous → retired. */
export type FlowStatus = 'draft' | 'paths_passed' | 'live' | 'previous' | 'retired'

/** One version of one workflow, `<lender>.<product>.flow` (G8). */
export interface WorkflowVersion {
  id: string
  lenderSlug: string
  /** e.g. moneyview.pl.flow */
  name: string
  productSlug: string
  version: string
  status: FlowStatus
  /** which API batch it runs: the live one (moves with C1) or one pinned version (drafts) */
  batch: { mode: 'live' } | { mode: 'pinned'; version: string }
  nodes: FlowNode[]
  edges: FlowEdge[]
  settings: { waitPerCallS: number; totalWaitS: number }
  pathsRun: { total: number; ok: number; at: string; by: string } | null
  savedBy: string
  savedAt: string
  note: string
  madeFrom: string | null
  madeLiveAt: string | null
}

/** The address the web app calls (`POST /v1/applications` with `endpoint_id`): exactly one workflow version behind it (G9). */
export interface ServiceEndpoint {
  /** endpoint_id, e.g. moneyview.personal */
  id: string
  lenderSlug: string
  productSlug: string
  workflowName: string
  /** the one version behind it */
  workflowVersion: string
  env: 'uat' | 'uat_prod'
  /** what an admin set; the lender switch and the product switch are applied on top (effectiveState) */
  state: 'active' | 'suspended' | 'retired'
  suspendReason: string | null
  stats: { runs24h: number; avgMs: number | null; errors24h: number; errorsWindow: number }
  lastSwitch: { at: string; from: string; to: string; by: string } | null
  createdAt: string
}

export interface RunStep {
  node: string
  kind: FlowNodeKind
  /** api: the endpoint route, then the lender call it made: "/v2/dedupe → POST /lead/dedupe" (no host, no personal data) */
  call?: string
  outcome: string
  replyCode?: string
  ms: number
  note?: string
}

/** One run in the 30-day log: reference ids only, PI masked, not a lead store. */
export interface WorkflowRun {
  id: string
  at: string
  endpointId: string
  lenderSlug: string
  workflowVersion: string
  batchVersion: string
  env: 'uat' | 'production'
  source: 'app' | 'website' | 'test call'
  steps: RunStep[]
  ms: number
  answer: AnswerKind
  /** the app's reference, shortened, e.g. app-7f3… */
  appRef: string
  logId: string
}

/** The standard answer (G10) the web app receives. */
export interface StandardAnswer {
  status: AnswerKind
  lender_ref: string | null
  offers: { amount: number; rate_pa: number; tenure_months: number; emi?: number }[]
  links: { continue: string | null }
  message: string
  lender_reply: { masked: true; log_id: string }
}

export interface PortalSettings {
  runLogDays: number
  errorRule: { errors: number; minutes: number }
  /** the sandbox and the live containers: memory per container, time per lender call */
  runner: { memoryMb: number; deadlineMs: number }
  /** the Hub's public addresses (API catalogue, Postman, OpenAPI): {address}/lenders/{slug}{route} and {address}/v1/applications */
  hubUrls: { uat: string; production: string }
}
